import './trades-panel.scss'

import styled from '@emotion/styled'
import { useNavigate } from 'react-router-dom'

import { CheckboxWithLabel } from '@/components/core-v2'
import { LoadingSpinner, Space, TextInput, Typography } from '@/components/core-v2/atoms'
import Icon from '@/components/core-v2/atoms/Icon'
import LinkIcon from '@/components/core-v2/molecules/linkIcon'
import {
  TablePagination,
  TablePaginationParams
} from '@/components/core-v2/molecules/table-pagination'
import { UserDisplayInfo } from '@/types/user'
import { blockKeys } from '@/utilities/input'

import { TradeSearchFilter } from '../draft-trades/state/draft-trades-slice'
import TradesView from '../draft-trades/templates/tradesView'
import { TradingAccount } from '../draft-trades/types'
import { actions as rawExistingNoteActions } from '../tradebuilder-v2/modules/01-note/existing-note/existing-note-slice'
import { actions as rawNoteActions } from '../tradebuilder-v2/modules/01-note/note/note-step-slice'
import { useActions } from '../tradebuilder-v2/store/use-actions'

export type TradesPanelProps = {
  accounts: TradingAccount[]
  isLoading: boolean
  onPaginationChange: (updatedPagination: TablePaginationParams) => void
  onSearch: (updatedFilter: TradeSearchFilter) => void
  paginationParams: TablePaginationParams
  searchFilter: TradeSearchFilter
  totalNumAccounts: number
  userInfo: UserDisplayInfo
}

export const TradesPanel = ({
  accounts,
  isLoading,
  onPaginationChange,
  onSearch,
  paginationParams,
  searchFilter,
  totalNumAccounts,
  userInfo
}: TradesPanelProps) => {
  const navigate = useNavigate()
  const existingNoteActions = useActions(rawExistingNoteActions)
  const noteActions = useActions(rawNoteActions)
  const onSearchText = (searchText: any) => {
    const clonedFilter = { ...searchFilter }
    clonedFilter.searchTerm = searchText
    onSearch(clonedFilter)
  }

  const onShowActiveOnlyChange = (checked: boolean) => {
    const clonedFilter = { ...searchFilter }
    clonedFilter.showLiveTrades = checked
    onSearch(clonedFilter)
  }
  const onShowNewTradesOnlyChange = (checked: boolean) => {
    const clonedFilter = { ...searchFilter }
    clonedFilter.showNewTrades = checked
    onSearch(clonedFilter)
  }

  const StyledPagination = styled.div`
    border-top: solid 1px var(--White95);
  `

  return (
    <Space isflex flexDirection="column">
      <Space mt={6}>
        <TextInput
          key="txt_trade_search"
          hideBorder
          maxLength={64}
          value={searchFilter.searchTerm}
          onDebouncedValue={onSearchText}
          onKeyDown={blockKeys('\\')}
        />
      </Space>
      <Space isflex className="trade-count-panel" justifyContent="space-between" pt={6}>
        <Space>
          {!isLoading && (
            <Space pt={1}>
              <Typography variant="sh2">{totalNumAccounts} draft trades found</Typography>
            </Space>
          )}
          <LoadingSpinner show={isLoading} />
        </Space>
        {userInfo.userRoles.some(x =>
          ['Administrator', 'BankAdmin', 'Editor', 'FLStructuring', 'Sales'].includes(x)
        ) && (
          <LinkIcon
            disabled={false}
            icon={<Icon name="circleAdd" size="default" />}
            iconPosition="left"
            text="Add a new trade"
            onClick={() => {
              existingNoteActions.reset()
              noteActions.reset()
              navigate('/trades/build')
            }}
          />
        )}
      </Space>
      <Space isflex justifyContent="flex-end" py={5}>
        <Space mr={4}>
          <CheckboxWithLabel
            checked={searchFilter.showNewTrades}
            onChange={onShowNewTradesOnlyChange}
          >
            New trades
          </CheckboxWithLabel>
        </Space>
        <CheckboxWithLabel checked={searchFilter.showLiveTrades} onChange={onShowActiveOnlyChange}>
          Live trades
        </CheckboxWithLabel>
      </Space>

      <StyledPagination>
        <Space my={4}>
          <TablePagination pagination={paginationParams} onChange={onPaginationChange} />
        </Space>
      </StyledPagination>

      <TradesView key="trades_view" accounts={accounts} userInfo={userInfo} />
    </Space>
  )
}
